import { useMemo, useState } from 'react';

import {
  BookOpenTextIcon,
  FileTextIcon,
  FolderPlusIcon,
  GlobeIcon,
  MessagesSquareIcon,
  PlusIcon,
  UploadIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { DataTable } from '@/components/ui/table/data-table';
import { TabsContent } from '@/components/ui/tabs';
import useDatasources from '@/hooks/useDatasources';
import { useUrlPagination } from '@/hooks/useUrlTablePagination';
import { useDownloadAsZip } from '@/modules/collections/hooks/useDownloadAsZip';
import {
  closeDeleteDocumentsModal,
  openDeleteDocumentsModal,
  setSelectedDocumentsForDeletion,
} from '@/redux/collections/actions';
import { selectSelectedDocumentsForDeletion } from '@/redux/collections/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import { setDatasourceId, setDocumentId } from '@/redux/session/actions';
import { selectAccountSlug, selectDocumentId } from '@/redux/session/selectors';
import { setSidebarContent } from '@/redux/sidebar/actions';
import { CollectionDocument, DatasourceType } from '@/shared/types/collections';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { BULK_DELETE_CONCURRENCY, runWithConcurrency } from '@/util/concurrency';

import { columns } from './columns';
import { CreateFolder } from './CreateFolder';
import { DATASOURCE_TYPE_TO_TAB, ROOT_DATASOURCE_NAMES } from '../../../constants';
import { downloadFaqsAsCsv, isDocumentRow, getKbRowAction } from '../../../helper';
import { useAPIDatasourcesDocuments } from '../../../hooks/useAPIDatasourcesDocuments';
import { useAPIDocuments } from '../../../hooks/useAPIDocuments';
import { useTabCategory } from '../../../hooks/useCategory';
import { useFilteredBySearch } from '../../../hooks/useFilteredBySearch';
import { useHashDocumentId } from '../../../hooks/useHashDocumentId';
import { DeleteDocumentsModal } from '../../../modals/DeleteDocumentsModal';
import { DatasourceDocument } from '../../../types';
import { DocumentsTableToolbar } from '../DocumentsTableToolbar';
import { TableEffects } from '../TableEffects';

const EmptyState = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'collections', actions.WRITE)
  );

  const resetReduxState = () => {
    dispatch(setDatasourceId(null));
    dispatch(setDocumentId(null));
  };

  return (
    <>
      <TabsContent value="faq" className="flex flex-1 flex-col">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <MessagesSquareIcon aria-hidden />
            </EmptyMedia>
            <EmptyTitle>{t('collections.empty_state.faq.title')}</EmptyTitle>
            <EmptyDescription>{t('collections.empty_state.faq.description')}</EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <div className="flex items-center gap-3">
              <Button
                variant="outline-neutral"
                size="sm"
                onClick={() => {
                  resetReduxState();
                  dispatch(setSidebarContent('upload_faq'));
                }}
                disabled={!canWrite}
              >
                <UploadIcon size={16} />
                {t('bundles.upload')}
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  resetReduxState();
                  dispatch(setSidebarContent('add_faq'));
                }}
                disabled={!canWrite}
              >
                <PlusIcon size={16} />
                {t('common.create')}
              </Button>
            </div>
          </EmptyContent>
        </Empty>
      </TabsContent>

      <TabsContent value="documents" className="flex flex-1 flex-col">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <FileTextIcon aria-hidden />
            </EmptyMedia>
            <EmptyTitle>{t('collections.empty_state.documents.title')}</EmptyTitle>
            <EmptyDescription>
              {t('collections.empty_state.documents.description')}
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <div className="flex items-center gap-3">
              <Button
                variant="outline-neutral"
                size="sm"
                onClick={() => {
                  resetReduxState();
                  dispatch(setSidebarContent('upload_documents'));
                }}
                disabled={!canWrite}
              >
                <UploadIcon size={16} />
                {t('bundles.upload')}
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  resetReduxState();
                  dispatch(setSidebarContent('add_txt_document'));
                }}
                disabled={!canWrite}
              >
                <PlusIcon size={16} />
                {t('common.create')}
              </Button>
            </div>
          </EmptyContent>
        </Empty>
      </TabsContent>

      <TabsContent value="webpages" className="flex flex-1 flex-col">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <GlobeIcon aria-hidden />
            </EmptyMedia>
            <EmptyTitle>{t('collections.empty_state.webpages.title')}</EmptyTitle>
            <EmptyDescription>{t('collections.empty_state.webpages.description')}</EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button
              size="sm"
              onClick={() => {
                resetReduxState();
                dispatch(setSidebarContent('add_webpages'));
              }}
              disabled={!canWrite}
            >
              <PlusIcon size={16} />
              {t('common.add')}
            </Button>
          </EmptyContent>
        </Empty>
      </TabsContent>

      <TabsContent value="external" className="flex flex-1 flex-col">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <BookOpenTextIcon aria-hidden />
            </EmptyMedia>
            <EmptyTitle>{t('collections.empty_state.external.title')}</EmptyTitle>
            <EmptyDescription>{t('collections.empty_state.external.description')}</EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button
              size="sm"
              onClick={() => {
                resetReduxState();
                dispatch(setSidebarContent('add_external_sources'));
              }}
              disabled={!canWrite}
            >
              <PlusIcon size={16} />
              {t('common.add')}
            </Button>
          </EmptyContent>
        </Empty>
      </TabsContent>
    </>
  );
};

export function DatasourcesDocumentsTable() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { slug, collectionId } = useParams();

  const [search, setSearch] = useState('');
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [createFolderDropdownOpen, setCreateFolderDropdownOpen] = useState(false);

  const { isFAQTab, isDocumentsTab, isWebsiteTab, isExternalTab, category } = useTabCategory();

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'collections', actions.WRITE)
  );
  const canDelete = useSelector((state: RootState) =>
    getPermissions(state, 'collections', actions.DELETE)
  );
  const canRead = useSelector((state: RootState) =>
    getPermissions(state, 'collections', actions.READ)
  );
  const accountSlug = useSelector(selectAccountSlug);
  const activeDocumentId = useSelector(selectDocumentId);
  const documentsForDeletion = useSelector(selectSelectedDocumentsForDeletion);
  const highlightedDocumentId = useHashDocumentId();

  const { data, isDatasourcesDocumentsLoading, getStatus } = useAPIDatasourcesDocuments({
    collection_id: collectionId,
    datasourceTab: category,
  });
  const { datasources: allDatasources, createDatasource } = useDatasources(collectionId);
  const { updateDocument, moveDocument, deleteDocumentAsync } = useAPIDocuments({
    collection_id: collectionId,
  });
  const { downloadAsZip } = useDownloadAsZip();
  const { pagination, setPagination } = useUrlPagination();

  const tableColumns = useMemo(
    () => columns({ updateDocument, canWrite, category }),
    [updateDocument, canWrite, category]
  );

  const datasources = useMemo(
    () =>
      allDatasources?.filter(
        (ds) =>
          (isFAQTab && ds.type === DatasourceType.FAQ && ds.name !== ROOT_DATASOURCE_NAMES.faq) ||
          (isDocumentsTab &&
            ds.type === DatasourceType.FILES &&
            ds.name !== ROOT_DATASOURCE_NAMES.documents)
      ),
    [allDatasources, isFAQTab, isDocumentsTab]
  );

  const rootDatasource = useMemo(() => {
    const rootName = isFAQTab ? ROOT_DATASOURCE_NAMES.faq : ROOT_DATASOURCE_NAMES.documents;
    return allDatasources?.find((ds) => ds.name === rootName);
  }, [allDatasources, isFAQTab]);

  // Filtered before the shared provider sees it so the footer count keys off the filtered length.
  const filteredData = useFilteredBySearch(data, search);

  // Only show the empty state after a successful fetch confirms no data.
  const showEmptyState =
    getStatus === 'success' &&
    (data?.length === 0 || (data?.length === 1 && data[0]?.document_count === 0));

  const resetReduxState = () => {
    dispatch(setDocumentId(null));
    dispatch(setDatasourceId(null));
  };

  const handleDocumentClick = (item: DatasourceDocument) => {
    resetReduxState();

    // Prevent opening the sidebar if the user doesn't have read permissions
    if (!canRead) {
      return;
    }

    if (isFAQTab) {
      dispatch(setDatasourceId(item.datasource_id));
      dispatch(setDocumentId(item.document_id));
      dispatch(setSidebarContent('add_faq'));
    } else if (isDocumentsTab) {
      dispatch(setDatasourceId(item.datasource_id));
      dispatch(setDocumentId(item.document_id));
      dispatch(setSidebarContent(item.type === 'txt' ? 'add_txt_document' : 'edit_document'));
    }
  };

  // Selected rows are always document rows (enableRowSelection excludes folders). DatasourceDocument
  // and CollectionDocument are sibling view types with incompatible fields (e.g. status), so the
  // compiler can't narrow structurally — assert once here, guarded by the isDocumentRow filter.
  const toCollectionDocuments = (items: DatasourceDocument[]) =>
    items.filter(isDocumentRow) as unknown as CollectionDocument[];

  const handleBulkDownload = async (selectedItems: CollectionDocument[]) => {
    if (isFAQTab) {
      await downloadFaqsAsCsv(selectedItems, collectionId, accountSlug);
    } else {
      await downloadAsZip(selectedItems, collectionId, accountSlug);
    }
  };

  const handleCreateFolderSubmit = (
    formData: { name: string },
    selectedItems: CollectionDocument[]
  ) => {
    if (!rootDatasource) {
      return;
    }
    // Note: formData.name is already validated for uniqueness by the CreateFolder form
    createDatasource(
      {
        name: formData.name,
        type: isFAQTab ? DatasourceType.FAQ : DatasourceType.FILES,
        collection_id: collectionId,
        config: {},
      },
      {
        onSuccess: (newDatasource) => {
          selectedItems.forEach((item, index) => {
            // Stagger the moves slightly to avoid overwhelming the browser
            setTimeout(() => {
              moveDocument({
                document_id: item.document_id,
                new_datasource_id: newDatasource.datasource_id,
                datasource_id: rootDatasource.datasource_id,
                name: item.name,
              });
            }, index * 100);
          });
          dispatch(setSidebarContent(null));
        },
        onSettled: () => {
          setCreateFolderDropdownOpen(false);
        },
      }
    );
  };

  const handleMoveToFolder = (
    selectedItems: CollectionDocument[],
    formData: { datasource_id: string }
  ) => {
    if (!rootDatasource) {
      return;
    }
    selectedItems.forEach((item, index) => {
      setTimeout(() => {
        moveDocument({
          document_id: item.document_id,
          new_datasource_id: formData.datasource_id,
          datasource_id: rootDatasource.datasource_id,
          name: item.name,
        });
      }, index * 100);
    });

    dispatch(setSidebarContent(null));
  };

  const handleBulkDelete = (selectedItems: CollectionDocument[]) => {
    dispatch(setSelectedDocumentsForDeletion(selectedItems));
    dispatch(openDeleteDocumentsModal());
  };

  const handleUpload = () => {
    resetReduxState();

    if (isFAQTab) {
      dispatch(setSidebarContent('upload_faq'));
    } else if (isDocumentsTab) {
      dispatch(setSidebarContent('upload_documents'));
    }
  };

  const handleCreate = () => {
    resetReduxState();

    if (isFAQTab) {
      dispatch(setSidebarContent('add_faq'));
    } else if (isWebsiteTab) {
      dispatch(setSidebarContent('add_webpages'));
    } else if (isExternalTab) {
      dispatch(setSidebarContent('add_external_sources'));
    } else if (isDocumentsTab) {
      dispatch(setSidebarContent('add_txt_document'));
    }
  };

  const handleConfirmDelete = async () => {
    if (!rootDatasource) {
      return;
    }
    setIsBulkDeleting(true);
    try {
      await runWithConcurrency(documentsForDeletion, BULK_DELETE_CONCURRENCY, (item) =>
        deleteDocumentAsync({
          document_id: item.document_id,
          datasource_id: rootDatasource.datasource_id,
        })
      );
      // Close the sidebar after successful deletion
      dispatch(setSidebarContent(null));
    } finally {
      dispatch(closeDeleteDocumentsModal());
      setIsBulkDeleting(false);
    }
  };

  // Folder rows are navigation links; document rows are sidebar-openers handled via onRowClick.
  const getRowHref = (item: DatasourceDocument) => {
    if (isDocumentRow(item)) {
      return null;
    }
    return `/${slug}/knowledge-bases/${item.collection_id}/category/${DATASOURCE_TYPE_TO_TAB[item.datasource_type]}/${item.datasource_id}`;
  };

  if (showEmptyState) {
    return <EmptyState />;
  }

  return (
    <>
      <DataTable.Root<DatasourceDocument, unknown>
        fill
        className="min-h-0 flex-1"
        columns={tableColumns}
        data={filteredData}
        rowCount={filteredData.length}
        isLoading={isDatasourcesDocumentsLoading}
        resourceName="files"
        enableRowHover
        getRowId={(row) => row.id}
        enableRowSelection={(row) => !isWebsiteTab && !isExternalTab && isDocumentRow(row.original)}
        getRowHref={getRowHref}
        getRowClickable={(row) => getKbRowAction(row, category) === 'edit'}
        onRowClick={handleDocumentClick}
        activeRowId={highlightedDocumentId ?? activeDocumentId ?? undefined}
        initialPageIndex={pagination.pageIndex}
        onPageChange={(pageIndex) => setPagination((prev) => ({ ...prev, pageIndex }))}
        emptyStateMessage={t('analytics.no_data')}
      >
        <DataTable.Toolbar<DatasourceDocument>>
          {(table) => (
            <DocumentsTableToolbar
              table={table}
              search={search}
              onSearchChange={setSearch}
              permissions={{ canRead, canWrite, canDelete }}
              datasources={datasources}
              onBulkDownload={(items) => handleBulkDownload(toCollectionDocuments(items))}
              onMoveToFolder={(items, formData) =>
                handleMoveToFolder(toCollectionDocuments(items), formData)
              }
              onBulkDelete={(items) => handleBulkDelete(toCollectionDocuments(items))}
              onUpload={handleUpload}
              onCreate={handleCreate}
              renderSelectionActions={(items) => (
                <DropdownMenu
                  open={createFolderDropdownOpen}
                  onOpenChange={setCreateFolderDropdownOpen}
                >
                  <InfoTooltip content={t('collections.create_folder_tooltip')}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost-neutral"
                        size="icon-sm"
                        aria-label={t('collections.create_folder_tooltip')}
                        disabled={!canWrite}
                      >
                        <FolderPlusIcon />
                      </Button>
                    </DropdownMenuTrigger>
                  </InfoTooltip>
                  <DropdownMenuContent className="w-xs" align="start">
                    <CreateFolder
                      collectionId={collectionId}
                      onSubmit={(formData) => {
                        handleCreateFolderSubmit(formData, toCollectionDocuments(items));
                        table.resetRowSelection();
                      }}
                    />
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            />
          )}
        </DataTable.Toolbar>

        <TableEffects data={filteredData} />
        <DataTable.Content />
        <DataTable.Pagination />
      </DataTable.Root>

      <DeleteDocumentsModal onConfirm={handleConfirmDelete} isDeleting={isBulkDeleting} />
    </>
  );
}
